import {inject, NgModule, provideAppInitializer} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {AppComponent} from './app.component';
import '@angular/common/locales/global/de';
import {HeaderComponent} from "./component/header/header.component";
import {HomeComponent} from "./home/home.component";
import {AppRoutingModule} from "./app.routing.module";
import {EnbwTruncatePipeModule} from "./service/EnbwTruncatePipeModule";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatListModule} from "@angular/material/list";
import {MatTabsModule} from "@angular/material/tabs";
import {MatButtonModule} from "@angular/material/button";
import {MatButtonToggleModule} from "@angular/material/button-toggle";
import {
  ConfirmationDialogComponent
} from "./component/dialog/confirmation-dialog/confirmation-dialog.component";
import {MatDialogModule} from "@angular/material/dialog";
import {MatSnackBarModule} from "@angular/material/snack-bar";
import {PopupSnackbarComponent} from "./component/snackbar/popup-snackbar.component";
import {MatIconModule} from "@angular/material/icon";
import {MatTooltipModule} from "@angular/material/tooltip";
import {MatInputModule} from "@angular/material/input"
import {MatMenuModule} from "@angular/material/menu";
import {MatSelectModule} from "@angular/material/select";


import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {UserComponent} from "./component/header/user/user.component";
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionStatus,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication
} from "@azure/msal-browser";
import {environment} from "../environments/environment";
import {catchError, filter, firstValueFrom, of, take, timeout} from "rxjs";

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/translations/', '.json')
}

/**
 * MSAL App Initializer — waits for MSAL to finish any in-progress interaction
 * before the app bootstraps. Uses `provideAppInitializer` (Angular 19+) which
 * replaces the deprecated `APP_INITIALIZER` token.
 *
 * Timeout after 2 seconds handles redirect scenarios where the user is not yet
 * authenticated (e.g. incognito mode → redirect to Microsoft login). In that case
 * MSAL never reaches InteractionStatus.None before the redirect, so we let the
 * app continue and MsalGuard will trigger the login redirect.
 */
function msalInitializer(): Promise<unknown> {
  const msalBroadcastService = inject(MsalBroadcastService);
  return firstValueFrom(
      msalBroadcastService.inProgress$.pipe(
          filter(status => status === InteractionStatus.None),
          take(1),
          timeout(2000),
          catchError(() => of(undefined))
      )
  );
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: `https://login.microsoftonline.com/${environment.msalConfig.auth.tenantId}`,
      redirectUri: environment.msalConfig.auth.redirectUri,
      postLogoutRedirectUri: environment.msalConfig.auth.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: (_, message, containsPii) => {
          if (!containsPii) console.error(message);
        },
        logLevel: LogLevel.Error,
        piiLoggingEnabled: false,
      },
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map([
      [environment.apiUrl, environment.msalConfig.api.resourceScopes]
    ])
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ["openid", ...environment.msalConfig.api.resourceScopes],
    },
    loginFailedRoute: '/login-failed',
  };
}

@NgModule({
  bootstrap: [AppComponent, MsalRedirectComponent],
  declarations: [
    AppComponent,
    ConfirmationDialogComponent,
    PopupSnackbarComponent,
  ], imports: [
    HeaderComponent,
    HomeComponent,
    UserComponent,
    BrowserModule,
    AppRoutingModule,
    MatToolbarModule,
    MatListModule,
    MatTabsModule,
    MatButtonModule,
    MatButtonToggleModule,
    EnbwTruncatePipeModule,
    MatDialogModule,
    MatSelectModule,
    MatSnackBarModule,
    MatIconModule,
    MatTooltipModule,
    FormsModule,
    MatMenuModule,
    MatInputModule,
    ReactiveFormsModule,
    MsalModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
  ], providers: [
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideAppInitializer(msalInitializer),
  ]
})

export class AppModule {
}
